import { Button, IconButton } from '@assembly-js/design-system';
import { t } from 'i18next';
import React from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

/**
 * A way back to the surface the paywall replaced. Given only where the paywall
 * took over a dialog the user was already in, so returning has somewhere to go.
 */
export interface AppLimitBackTo {
  /** Reads as "Back to <app>"; the caller builds the whole phrase. */
  label: string;
  onBack: () => void;
}

interface AppLimitModalShellProps {
  /** Names the dialog for assistive tech; each route uses its own heading. */
  ariaLabel: string;
  /**
   * Suppresses dismissal. Set while an irreversible step (a card charge) is in
   * flight, so closing cannot strand the user mid-payment.
   */
  isDismissDisabled?: boolean;
  /**
   * Renders the Back row above the content. Absent on the surfaces that raise
   * the paywall from a page, where there is no dialog to return to.
   */
  backTo?: AppLimitBackTo;
  /** Desktop width; the combined checkout is drawn wider to fit its breakdown. */
  widthPx?: number;
  onClose: () => void;
  children: React.ReactNode;
}

/** The fee and upgrade routes share one width — the design draws them at the same size. */
const DEFAULT_MODAL_WIDTH_PX = 400;

/** ModalWrapper's paper is squarer and heavier than the design's card. */
const DESKTOP_PAPER_CLASS = [
  '[&_.MuiDialog-paper]:rounded-lg',
  '[&_.MuiDialog-paper]:shadow-popover-050',
].join(' ');

/**
 * The phone design is an edge-to-edge sheet, which is why it carries no corner
 * radius. `fullScreen` gets it there, but both the radius ModalWrapper's own
 * paper style sets and the width it sets from MUI's `sm` breakpoint up (600px,
 * inside the ≤640px this treats as mobile) land in the same specificity tier as
 * MUI's fullScreen class, so which wins comes down to stylesheet order. These
 * override the paper directly rather than leaving that to chance.
 */
const MOBILE_PAPER_CLASS = [
  '[&_.MuiDialog-paper]:rounded-none',
  '[&_.MuiDialog-paper]:w-full',
  '[&_.MuiDialog-paper]:max-w-full',
].join(' ');

/**
 * The chrome both app-limit paywalls sit in. The routes differ in everything
 * inside — the fee route is a compact confirm, the upgrade route a plan picker
 * — but the design gives them one corner close button in the same spot, so the
 * shell owns it rather than each route placing its own.
 */
export const AppLimitModalShell: React.FC<AppLimitModalShellProps> = ({
  ariaLabel,
  isDismissDisabled = false,
  backTo,
  widthPx = DEFAULT_MODAL_WIDTH_PX,
  onClose,
  children,
}) => {
  const isMobile = useMediaQuery('sm', 'max');

  const handleClose: ModalWrapperProps['onClose'] = () => {
    if (isDismissDisabled) return;
    onClose();
  };

  return (
    <ModalWrapper
      open
      onClose={handleClose}
      hideActions
      hideDividers
      disableGutters
      fullScreen={isMobile}
      fullWidth={isMobile}
      width={isMobile ? undefined : widthPx}
      aria-label={ariaLabel}
      className={isMobile ? MOBILE_PAPER_CLASS : DESKTOP_PAPER_CLASS}
    >
      <div className="relative bg-white">
        <IconButton
          icon="Close"
          variant="minimal"
          size="sm"
          aria-label={t(($) => $.page_apps.app_builder.app_limit_modal.close)}
          onClick={onClose}
          disabled={isDismissDisabled}
          // Floats over the content in the corner the design draws it in, so a
          // route's own layout never has to leave a column for it.
          className="absolute right-3 top-3 z-10 size-7 rounded hover:bg-gray-150 sm:size-6"
        />
        {backTo && (
          // Clipped rather than wrapped: a long app name would otherwise run
          // under the close button floating in the same corner.
          <div className="overflow-hidden px-4 pt-4 sm:px-6">
            <Button
              variant="text"
              size="sm"
              prefixIcon="ArrowLeft"
              label={backTo.label}
              onClick={backTo.onBack}
              disabled={isDismissDisabled}
              className="min-w-0 max-w-[calc(100%-2rem)] [&>span]:truncate"
            />
          </div>
        )}
        {children}
      </div>
    </ModalWrapper>
  );
};
